import { Component } from '@theme/component';
import { fetchConfig, debounce, onAnimationEnd, prefersReducedMotion, resetShimmer } from '@theme/utilities';
import { morphSection, sectionRenderer } from '@theme/section-renderer';
import { morph } from '@theme/morph';
import {
  ThemeEvents,
  CartUpdateEvent,
  QuantitySelectorUpdateEvent,
  CartAddEvent,
  DiscountUpdateEvent,
} from '@theme/events';
import { cartPerformance } from '@theme/performance';

const DRAWER_CHANGE_TIMEOUT = 8000;
const DRAWER_RECOVERY_TIMEOUT = 4500;

/** @typedef {import('./utilities').TextComponent} TextComponent */

/**
 * A custom element that displays a cart items component.
 *
 * @typedef {object} Refs
 * @property {HTMLElement[]} quantitySelectors - The quantity selector elements.
 * @property {HTMLTableRowElement[]} cartItemRows - The cart item rows.
 * @property {TextComponent} cartTotal - The cart total.
 *
 * @extends {Component<Refs>}
 */
class CartItemsComponent extends Component {
  #debouncedOnChange = debounce(this.#onQuantityChange, 300).bind(this);

  connectedCallback() {
    super.connectedCallback();

    document.addEventListener(ThemeEvents.cartUpdate, this.#handleCartUpdate);
    document.addEventListener(ThemeEvents.discountUpdate, this.handleDiscountUpdate);
    document.addEventListener(ThemeEvents.quantitySelectorUpdate, this.#debouncedOnChange);
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    document.removeEventListener(ThemeEvents.cartUpdate, this.#handleCartUpdate);
    document.removeEventListener(ThemeEvents.discountUpdate, this.handleDiscountUpdate);
    document.removeEventListener(ThemeEvents.quantitySelectorUpdate, this.#debouncedOnChange);
  }

  /**
   * Handles QuantitySelectorUpdateEvent change event.
   * @param {QuantitySelectorUpdateEvent} event - The event.
   */
  #onQuantityChange(event) {
    if (!(event.target instanceof Node) || !this.contains(event.target)) return;

    const { quantity, cartLine: line } = event.detail;

    // Cart items require a line number
    if (!line) return;

    if (quantity === 0) {
      return this.onLineItemRemove(line);
    }

    this.updateQuantity({
      line,
      quantity,
      action: 'change',
    });
    const lineItemRow = this.refs.cartItemRows[line - 1];

    if (!lineItemRow) return;

    const textComponent = /** @type {TextComponent | undefined} */ (lineItemRow.querySelector('text-component'));
    textComponent?.shimmer();
  }

  /**
   * Handles the line item removal.
   * @param {number} line - The line item index.
   */
  onLineItemRemove(line) {
    this.updateQuantity({
      line,
      quantity: 0,
      action: 'clear',
    });

    const cartItemRowToRemove = this.refs.cartItemRows[line - 1];

    if (!cartItemRowToRemove) return;

    const rowsToRemove = [
      cartItemRowToRemove,
      // Get all nested lines of the row to remove
      ...this.refs.cartItemRows.filter((row) => row.dataset.parentKey === cartItemRowToRemove.dataset.key),
    ];

    // Add class to the row to trigger the animation
    rowsToRemove.forEach((row) => {
      const remove = () => row.remove();

      if (prefersReducedMotion()) return remove();

      row.style.setProperty('--row-height', `${row.clientHeight}px`);
      row.classList.add('removing');

      // Remove the row after the animation ends
      onAnimationEnd(row, remove);
    });
  }

  /**
   * Updates the quantity.
   * @param {Object} config - The config.
   * @param {number} config.line - The line.
   * @param {number} config.quantity - The quantity.
   * @param {string} config.action - The action.
   */
  updateQuantity(config) {
    const cartPerformaceUpdateMarker = cartPerformance.createStartingMarker(`${config.action}:user-action`);
    const isDrawer = Boolean(this.closest('cart-drawer-component'));
    this.querySelector('[data-dj-cart-update-error]')?.remove();

    this.#disableCartItems();

    const { line, quantity } = config;
    const { cartTotal } = this.refs;

    const cartItemsComponents = document.querySelectorAll('cart-items-component');
    const sectionsToUpdate = new Set([this.sectionId]);
    cartItemsComponents.forEach((item) => {
      if (item instanceof HTMLElement && item.dataset.sectionId) {
        sectionsToUpdate.add(item.dataset.sectionId);
      }
    });

    const body = JSON.stringify({
      line: line,
      quantity: quantity,
      sections: Array.from(sectionsToUpdate).join(','),
      sections_url: window.location.pathname,
    });

    cartTotal?.shimmer();

    const request = isDrawer
      ? this.#readDrawerResponse(Theme.routes.cart_change_url, fetchConfig('json', { body }), DRAWER_CHANGE_TIMEOUT)
      : fetch(`${Theme.routes.cart_change_url}`, fetchConfig('json', { body })).then((response) => response.text());

    return request
      .then((responseText) => {
        const parsedResponseText = JSON.parse(responseText);

        resetShimmer(this);

        if (parsedResponseText.errors) {
          this.#handleCartError(line, parsedResponseText);
          // A remove animation may already have removed this row. Re-read the
          // authoritative cart even when Shopify explicitly rejects the change.
          if (isDrawer) throw new Error('Shopify rejected the cart change');
          return;
        }

        this.#applyCartResponse(parsedResponseText);
      })
      .catch(async (error) => {
        console.error(error);
        if (!isDrawer || !this.isConnected) return;

        // A timeout does not prove the mutation failed on Shopify. Never retry
        // the POST; read the current section once before enabling new actions.
        try {
          const url = new URL(window.location.href);
          url.searchParams.set('sections', this.sectionId);
          url.searchParams.set('_dj_cart_reconcile', String(Date.now()));
          const responseText = await this.#readDrawerResponse(
            url.toString(),
            { method: 'GET', cache: 'no-store' },
            DRAWER_RECOVERY_TIMEOUT
          );
          if (!this.isConnected) return;
          this.#applyCartResponse({ sections: JSON.parse(responseText) });
        } catch (recoveryError) {
          console.error(recoveryError);
          if (this.isConnected) {
            const notice = document.createElement('p');
            notice.setAttribute('data-dj-cart-update-error', '');
            notice.setAttribute('role', 'alert');
            notice.textContent = 'Det gick inte att uppdatera varukorgen. ';
            const reload = document.createElement('a');
            reload.href = window.location.href;
            reload.textContent = 'Ladda om för att se aktuellt innehåll.';
            notice.append(reload);
            (this.querySelector('.cart-drawer__summary') || this).prepend(notice);
          }
        }
      })
      .finally(() => {
        resetShimmer(this);
        this.#enableCartItems();
        cartPerformance.measureFromMarker(cartPerformaceUpdateMarker);
      });
  }

  /** Bounds both response headers and the complete body, including a stalled body read. */
  async #readDrawerResponse(url, options, timeoutMs) {
    const controller = new AbortController();
    let timer;
    const timeout = new Promise((_, reject) => {
      timer = window.setTimeout(() => {
        controller.abort();
        reject(new Error('Cart request timed out'));
      }, timeoutMs);
    });

    try {
      return await Promise.race([
        fetch(url, { ...options, signal: controller.signal }).then((response) => {
          if (!response.ok) throw new Error(`Cart request failed with status ${response.status}`);
          return response.text();
        }),
        timeout,
      ]);
    } finally {
      window.clearTimeout(timer);
    }
  }

  #applyCartResponse(parsedResponseText) {
    const sectionHTML = parsedResponseText?.sections?.[this.sectionId];
    if (typeof sectionHTML !== 'string' || !sectionHTML.trim()) {
      throw new Error('Cart response did not include its section');
    }
    const newSectionHTML = new DOMParser().parseFromString(sectionHTML, 'text/html');
    const currentDialog = this.closest('cart-drawer-component')?.querySelector('dialog');
    const nextDrawerCartItems = currentDialog
      ? newSectionHTML.querySelector('cart-drawer-component cart-items-component')
      : null;
    if (currentDialog && !nextDrawerCartItems) throw new Error('Cart response did not include the drawer');
    const preserveDrawerModal =
      currentDialog instanceof HTMLDialogElement &&
      currentDialog.matches(':modal') &&
      nextDrawerCartItems instanceof HTMLElement;
    const drawerItemsScrollTop = currentDialog?.querySelector('.cart-drawer__items')?.scrollTop || 0;

    // Grab the new cart item count from a hidden element
    const newCartHiddenItemCount = newSectionHTML.querySelector('[ref="cartItemCount"]')?.textContent;
    const newCartItemCount = newCartHiddenItemCount ? parseInt(newCartHiddenItemCount, 10) : 0;

    // Update data-cart-quantity for all matching variants
    this.#updateQuantitySelectors(parsedResponseText);

    this.dispatchEvent(
      new CartUpdateEvent(parsedResponseText, this.sectionId, {
        itemCount: newCartItemCount,
        source: 'cart-items-component',
        sections: parsedResponseText.sections,
        preserveDrawerModal,
      })
    );

    if (preserveDrawerModal) {
      const currentRecommendations = this.querySelector('dj-cart-recommendations');
      const nextRecommendations = nextDrawerCartItems.querySelector('dj-cart-recommendations');
      const currentProducts = currentRecommendations?.querySelector('[data-dj-cart-products]');
      const nextProducts = nextRecommendations?.querySelector('[data-dj-cart-products]');
      if (currentProducts && nextProducts) currentProducts.textContent = nextProducts.textContent || '[]';

      if (currentRecommendations && nextRecommendations) {
        currentRecommendations.setAttribute('data-skip-subtree-update', '');
        nextRecommendations.setAttribute('data-skip-subtree-update', '');
      }

      try {
        morph(this, nextDrawerCartItems);
      } finally {
        currentRecommendations?.removeAttribute('data-skip-subtree-update');
      }
      this.updatedCallback();

      const currentSection = document.getElementById(`shopify-section-${this.sectionId}`);
      const currentTrigger = currentSection?.querySelector('[data-testid="cart-drawer-trigger"]');
      const nextTrigger = newSectionHTML.querySelector('[data-testid="cart-drawer-trigger"]');
      if (currentTrigger && nextTrigger) morph(currentTrigger, nextTrigger);

      const nextDrawerItems = currentDialog.querySelector('.cart-drawer__items');
      if (nextDrawerItems instanceof HTMLElement) nextDrawerItems.scrollTop = drawerItemsScrollTop;
    } else {
      morphSection(this.sectionId, parsedResponseText.sections[this.sectionId]);
    }

    this.#updateCartQuantitySelectorButtonStates();
  }

  /**
   * Handles the discount update.
   * @param {DiscountUpdateEvent} event - The event.
   */
  handleDiscountUpdate = (event) => {
    this.#handleCartUpdate(event);
  };

  /**
   * Handles the cart error.
   * @param {number} line - The line.
   * @param {Object} parsedResponseText - The parsed response text.
   * @param {string} parsedResponseText.errors - The errors.
   */
  #handleCartError = (line, parsedResponseText) => {
    const quantitySelector = this.refs.quantitySelectors[line - 1];
    const quantityInput = quantitySelector?.querySelector('input');

    if (!quantityInput) throw new Error('Quantity input not found');

    quantityInput.value = quantityInput.defaultValue;

    const cartItemError = this.refs[`cartItemError-${line}`];
    const cartItemErrorContainer = this.refs[`cartItemErrorContainer-${line}`];

    if (!(cartItemError instanceof HTMLElement)) throw new Error('Cart item error not found');
    if (!(cartItemErrorContainer instanceof HTMLElement)) throw new Error('Cart item error container not found');

    cartItemError.textContent = parsedResponseText.errors;
    cartItemErrorContainer.classList.remove('hidden');
  };

  /**
   * Handles the cart update.
   *
   * @param {DiscountUpdateEvent | CartUpdateEvent | CartAddEvent} event
   */
  #handleCartUpdate = (event) => {
    if (event instanceof DiscountUpdateEvent) {
      if (event.detail?.resource?.sections?.[this.sectionId]) return;
      sectionRenderer.renderSection(this.sectionId, { cache: false });
      return;
    }
    if (event.target === this) return;

    const cartItemsHtml = event.detail.data.sections?.[this.sectionId];
    if (cartItemsHtml) {
      morphSection(this.sectionId, cartItemsHtml);

      // Update button states for all cart quantity selectors after morph
      this.#updateCartQuantitySelectorButtonStates();
    } else {
      sectionRenderer.renderSection(this.sectionId, { cache: false });
    }
  };

  /**
   * Disables the cart items.
   */
  #disableCartItems() {
    this.classList.add('cart-items-disabled');
  }

  /**
   * Enables the cart items.
   */
  #enableCartItems() {
    this.classList.remove('cart-items-disabled');
  }

  /**
   * Updates quantity selectors for all matching variants in the cart.
   * @param {Object} updatedCart - The updated cart object.
   * @param {Array<{variant_id: number, quantity: number}>} [updatedCart.items] - The cart items.
   */
  #updateQuantitySelectors(updatedCart) {
    if (!updatedCart.items) return;

    for (const item of updatedCart.items) {
      const variantId = item.variant_id.toString();
      const selectors = document.querySelectorAll(`quantity-selector-component[data-variant-id="${variantId}"]`);

      for (const selector of selectors) {
        const input = selector.querySelector('input[data-cart-quantity]');
        if (!input) continue;

        input.setAttribute('data-cart-quantity', item.quantity.toString());

        // Update the quantity selector's internal state
        if ('updateCartQuantity' in selector && typeof selector.updateCartQuantity === 'function') {
          selector.updateCartQuantity();
        }
      }
    }
  }

  /**
   * Updates button states for all cart quantity selector components.
   */
  #updateCartQuantitySelectorButtonStates() {
    for (const selector of document.querySelectorAll('cart-quantity-selector-component')) {
      /** @type {any} */ (selector).updateButtonStates?.();
    }
  }

  /**
   * Gets the section id.
   * @returns {string} The section id.
   */
  get sectionId() {
    const { sectionId } = this.dataset;

    if (!sectionId) throw new Error('Section id missing');

    return sectionId;
  }
}

if (!customElements.get('cart-items-component')) {
  customElements.define('cart-items-component', CartItemsComponent);
}
